<template>
  <v-dialog :value="value" max-width="920" scrollable @input="$emit('input', $event)">
    <v-card class="pa-4">
      <div class="text-h6 mb-1">Carga masiva</div>
      <p class="hint">
        Elige la carpeta. Dentro va el CSV y, juntos, los videos, audios e imágenes.
        Primero ves el curso, sus niveles y lecciones. Solo al aceptar se sube a la app.
      </p>
      <p class="hint mono">
        Columnas: tipo, curso, marca, nivel, titulo, descripcion, archivo, miniatura, enunciado, palabra, opcion_1, opcion_2, opcion_3, texto
      </p>
      <p class="hint">
        Tipos por fila: <strong>curso</strong>, <strong>nivel</strong>, <strong>video_principal</strong>, <strong>video_explicacion</strong>,
        <strong>gramatica</strong>, <strong>habla</strong>, <strong>escribe</strong>, <strong>vocabulario</strong>.
        El examen no va en la carga masiva: se arma a mano en el KPI.
      </p>
      <p class="hint">En gramática escribe la frase completa y en palabra la correcta. El sistema quita esa palabra al mostrarla.</p>
      <button type="button" class="referencia" @click="abrirReferencia">Ver CSV de referencia</button>
      <v-dialog v-model="referenciaAbierta" max-width="860" scrollable>
        <v-card class="pa-4">
          <div class="text-h6 mb-1">CSV de referencia</div>
          <p class="hint">Curso con dos niveles. Cada nivel trae video principal, video de explicación y ejercicios. El examen se configura después a mano.</p>
          <article v-for="(nivel, ni) in referenciaNiveles" :key="'ref-' + ni" class="ref-nivel">
            <p class="kicker">{{ referenciaCurso }} · {{ nivel.nombre }}</p>
            <h4>{{ nivel.nombre }}</h4>
            <p class="desc">Principal {{ nivel.videoPrincipal || "—" }} · Explicación {{ nivel.videoExplicacion || "—" }}</p>
            <p class="ref-linea"><strong>Gramática.</strong> {{ nivel.gramatica.join(" · ") || "—" }}</p>
            <p class="ref-linea"><strong>Habla.</strong> {{ nivel.habla.join(" · ") || "—" }}</p>
            <p class="ref-linea"><strong>Escribe.</strong> {{ nivel.escribe.join(" · ") || "—" }}</p>
            <p class="ref-linea"><strong>Vocabulario.</strong> {{ nivel.vocabulario.join(" · ") || "—" }}</p>
          </article>
          <pre class="csv-ref">{{ csvReferencia }}</pre>
          <div class="acciones mt-3">
            <v-btn text @click="referenciaAbierta = false">Cerrar</v-btn>
            <v-spacer />
            <v-btn color="primary" :disabled="!csvReferencia" @click="descargarReferencia">Descargar</v-btn>
          </div>
        </v-card>
      </v-dialog>
      <input class="mt-2" type="file" accept=".csv,text/csv,.mp4,.m4a,.mp3,.png,.jpg,.jpeg,.webp,audio/*,video/*,image/*" webkitdirectory directory multiple @change="elegirCarpeta" />

      <v-alert v-if="error" type="error" dense class="mt-3">{{ error }}</v-alert>

      <div v-if="preview" class="preview mt-4">
        <article v-for="(curso, ci) in preview.cursos" :key="'c-' + ci" class="curso-bloque">
          <header class="curso-head">
            <div>
              <p class="marca">{{ curso.marca }}</p>
              <h3>{{ curso.nombre }}</h3>
            </div>
            <span v-if="curso.reescribe" class="aviso">Se reescribe</span>
          </header>
          <div class="nivel-grid">
            <article v-for="(nivel, ni) in curso.niveles" :key="'n-' + ci + '-' + ni" class="nivel-card">
              <div class="nivel-foto" :class="{ vacia: !(nivel.videoPrincipal && nivel.videoPrincipal.miniUrl) }">
                <img v-if="nivel.videoPrincipal && nivel.videoPrincipal.miniUrl" :src="nivel.videoPrincipal.miniUrl" alt="" />
                <span v-else>Sin portada</span>
              </div>
              <div class="nivel-cuerpo">
                <p class="kicker">Nivel <span v-if="nivel.reescribe">· se reescribe</span></p>
                <h4>{{ nivel.nombre }}</h4>
                <p class="desc">{{ nivel.descripcion || "Sin descripción del nivel" }}</p>
                <p class="ref-linea"><strong>Video principal.</strong> {{ nivel.videoPrincipal.titulo || "Sin crear" }} · {{ nivel.videoPrincipal.archivo || "sin archivo" }}</p>
                <p v-if="nivel.videoPrincipal.faltante" class="aviso">Falta el video {{ nivel.videoPrincipal.archivo }}</p>
                <p v-if="nivel.videoPrincipal.faltanteMini" class="aviso">Falta la portada {{ nivel.videoPrincipal.miniatura }}</p>
                <p class="ref-linea"><strong>Video de explicación.</strong> {{ nivel.videoExplicacion.titulo || "Sin crear" }} · {{ nivel.videoExplicacion.archivo || "sin archivo (opcional)" }}</p>
                <p v-if="nivel.videoExplicacion.faltante" class="aviso">Falta el video {{ nivel.videoExplicacion.archivo }}</p>
                <p v-if="nivel.videoExplicacion.faltanteMini" class="aviso">Falta la portada {{ nivel.videoExplicacion.miniatura }}</p>
                <div class="chips">
                  <span>Gramática {{ nivel.gramatica }}</span>
                  <span>Habla {{ nivel.habla }}</span>
                  <span>Escribe {{ nivel.escribe }}</span>
                  <span>Vocabulario {{ nivel.vocabulario }}</span>
                </div>
                <ul class="frases">
                  <li v-for="(frase, fi) in nivel.frases" :key="'f-' + fi">{{ frase }}</li>
                </ul>
                <div v-if="nivel.vocabFotos.length" class="vocab-fila">
                  <img v-for="(foto, vi) in nivel.vocabFotos" :key="'vf-' + vi" :src="foto" alt="" />
                </div>
                <p class="examen">El examen no se carga aquí. Se configura a mano en el KPI cuando el resto esté listo. Habla, Escribe y Vocabulario se liberan al aceptar.</p>
              </div>
            </article>
          </div>
        </article>
        <p v-if="repetidos.length" class="aviso">Archivos con el mismo nombre, se usa el último: {{ repetidos.join(", ") }}</p>
      </div>

      <div class="acciones mt-4">
        <v-btn text @click="$emit('input', false)">Cerrar</v-btn>
        <v-spacer />
        <v-btn color="primary" :disabled="!preview || subiendo" :loading="subiendo" @click="aceptar">
          Aceptar y subir a la app
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
function partirCsv(texto) {
  const filas = []
  let fila = []
  let celda = ""
  let comillas = false
  const src = String(texto || "").replace(/^\uFEFF/, "")
  for (let i = 0; i < src.length; i++) {
    const c = src.charAt(i)
    if (comillas) {
      if (c === '"') {
        if (src.charAt(i + 1) === '"') {
          celda += '"'
          i++
        } else comillas = false
      } else celda += c
      continue
    }
    if (c === '"') {
      comillas = true
      continue
    }
    if (c === ",") {
      fila.push(celda.trim())
      celda = ""
      continue
    }
    if (c === "\n") {
      fila.push(celda.trim())
      if (fila.some((x) => x)) filas.push(fila)
      fila = []
      celda = ""
      continue
    }
    if (c !== "\r") celda += c
  }
  fila.push(celda.trim())
  if (fila.some((x) => x)) filas.push(fila)
  return filas
}

function vacioVideo() {
  return { titulo: "", descripcion: "", archivo: "", miniatura: "", faltante: false, faltanteMini: false }
}

function vacioNivel(nombre) {
  return {
    nombre,
    descripcion: "",
    reescribe: false,
    videoPrincipal: vacioVideo(),
    videoExplicacion: vacioVideo(),
    gramaticaLista: [],
    explicacion: "",
    hablaLista: [],
    escribeLista: [],
    vocabLista: [],
  }
}

function vacioLeccion(nombre) {
  return {
    nombre: nombre || "",
    titulo: "",
    descripcion: "",
    archivo: "",
    miniatura: "",
    faltante: false,
    faltanteMini: false,
  }
}

export default {
  props: { value: Boolean },
  data() {
    return {
      error: "",
      preview: null,
      manifest: null,
      archivos: [],
      repetidos: [],
      subiendo: false,
      existentes: [],
      referenciaAbierta: false,
      csvReferencia: "",
      referenciaCurso: "",
      referenciaNiveles: [],
    }
  },
  methods: {
    abrirReferencia() {
      this.referenciaAbierta = true
      if (this.csvReferencia) return
      fetch("/carga-referencia.csv").then((r) => r.text()).then((texto) => {
        this.csvReferencia = texto
        this.leerReferencia(texto)
      }).catch(() => {
        this.csvReferencia = ""
      })
    },
    leerReferencia(texto) {
      const filas = partirCsv(texto)
      if (filas.length < 2) return
      const head = filas[0].map((h) => h.toLowerCase())
      const idx = (nombre) => head.indexOf(nombre)
      const niveles = []
      let curso = ""
      for (let i = 1; i < filas.length; i++) {
        const celdas = filas[i]
        const val = (nombre) => {
          const p = idx(nombre)
          return p >= 0 ? (celdas[p] || "") : ""
        }
        const tipo = val("tipo").toLowerCase()
        if (val("curso")) curso = val("curso")
        if (tipo === "nivel") {
          niveles.push({
            nombre: val("nivel"),
            videoPrincipal: "",
            videoExplicacion: "",
            gramatica: [],
            habla: [],
            escribe: [],
            vocabulario: [],
          })
        }
        let nivel = niveles[niveles.length - 1]
        if (val("nivel")) {
          nivel = niveles.find(function (n) { return n.nombre === val("nivel") }) || nivel
        }
        if (!nivel) continue
        if (tipo === "video_principal" || (tipo === "video" && !nivel.videoPrincipal)) {
          nivel.videoPrincipal = val("archivo") || val("titulo") || "sí"
        } else if (tipo === "video_explicacion") {
          nivel.videoExplicacion = val("archivo") || val("titulo") || "sí"
        } else if (tipo === "gramatica") {
          const partes = String(val("enunciado") || "").trim().split(/\s+/).filter(Boolean)
          const palabra = val("palabra")
          const pos = partes.findIndex((p) => p.toLowerCase() === palabra.toLowerCase())
          if (pos >= 0) partes[pos] = "______"
          nivel.gramatica.push(partes.join(" ") + " (" + palabra + ")")
        } else if (tipo === "habla" || tipo === "escribe") {
          nivel[tipo].push(val("texto") + " · " + val("archivo"))
        } else if (tipo === "vocabulario") {
          nivel.vocabulario.push([val("opcion_1"), val("opcion_2"), val("opcion_3")].filter(Boolean).join(", ") + " · " + val("archivo"))
        }
      }
      this.referenciaCurso = curso
      this.referenciaNiveles = niveles
    },
    descargarReferencia() {
      const blob = new Blob([this.csvReferencia], { type: "text/csv;charset=utf-8" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = "carga-referencia.csv"
      a.click()
      URL.revokeObjectURL(url)
    },
    nombreArchivo(f) {
      const ruta = (f && (f.webkitRelativePath || f.name)) || ""
      const partes = String(ruta).split(/[/\\]/)
      return partes[partes.length - 1] || ""
    },
    leerInicio(f) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ""))
        reader.onerror = () => resolve("")
        reader.readAsText(f.slice(0, 120))
      })
    },
    async elegirCarpeta(ev) {
      const files = Array.from((ev.target && ev.target.files) || [])
      this.error = ""
      this.preview = null
      this.manifest = null
      this.repetidos = []
      const nombres = {}
      const repetidos = []
      files.forEach((f) => {
        const clave = this.nombreArchivo(f).toLowerCase()
        if (!clave) return
        if (nombres[clave]) repetidos.push(clave)
        nombres[clave] = true
      })
      this.repetidos = repetidos
      let csv = files.find((f) => /\.csv$/i.test(this.nombreArchivo(f)))
      if (!csv) {
        for (let i = 0; i < files.length; i++) {
          const f = files[i]
          if (f.size > 1024 * 1024) continue
          if (/\.(mp4|m4a|mp3|png|jpe?g|webp|gif)$/i.test(this.nombreArchivo(f))) continue
          const inicio = await this.leerInicio(f)
          if (/tipo\s*,/i.test(inicio)) {
            csv = f
            break
          }
        }
      }
      if (!csv) {
        const vistos = files.slice(0, 6).map((f) => this.nombreArchivo(f)).filter(Boolean)
        this.error = files.length
          ? "No reconocí el CSV entre " + files.length + " archivos. Vi: " + vistos.join(", ")
          : "No llegaron archivos de esa carpeta. Vuelve a elegirla."
        return
      }
      this.archivos = files.filter((f) => f !== csv)
      const reader = new FileReader()
      reader.onload = () => {
        this.$http.get("curso.virtual.niveles").then((r) => {
          const data = r.data || r.body || {}
          this.existentes = data.niveles || []
          return this.marcarNiveles(this.existentes)
        }).catch(() => {
          this.existentes = []
        }).finally(() => {
          this.armar(String(reader.result || ""))
        })
      }
      reader.onerror = () => {
        this.error = "No se pudo leer el CSV"
      }
      reader.readAsText(csv)
    },
    marcarNiveles(cursos) {
      const pedidos = cursos.map((c) => {
        return this.$http.get("curso.virtual.curso.niveles/" + c.id).then((r) => {
          const data = r.data || r.body || {}
          c._niveles = (data.niveles || []).map((n) => String(n.nombre || "").toLowerCase())
        }).catch(() => {
          c._niveles = []
        })
      })
      return Promise.all(pedidos)
    },
    armar(texto) {
      const filas = partirCsv(texto)
      if (filas.length < 2) {
        this.error = "El CSV no tiene filas"
        return
      }
      const head = filas[0].map((h) => h.toLowerCase())
      const idx = (nombre) => head.indexOf(nombre)
      const cursos = {}
      const orden = []
      const presentes = new Set(this.archivos.map((f) => this.nombreArchivo(f).toLowerCase()))
      const archivoDe = (nombre) => this.archivos.find((f) => this.nombreArchivo(f).toLowerCase() === String(nombre || "").toLowerCase())
      const urlDe = (nombre) => {
        const f = archivoDe(nombre)
        if (!f) return ""
        const url = URL.createObjectURL(f)
        this._urls = this._urls || []
        this._urls.push(url)
        return url
      }
      ;(this._urls || []).forEach((url) => URL.revokeObjectURL(url))
      this._urls = []
      for (let i = 1; i < filas.length; i++) {
        const celdas = filas[i]
        const val = (nombre) => {
          const p = idx(nombre)
          return p >= 0 ? (celdas[p] || "") : ""
        }
        const tipo = val("tipo").toLowerCase()
        let archivo = val("archivo")
        let texto = val("texto")
        let op1 = val("opcion_1")
        let op2 = val("opcion_2")
        let op3 = val("opcion_3")
        if ((tipo === "habla" || tipo === "escribe" || tipo === "vocabulario") && !archivo && /\.(m4a|mp3|wav|ogg|png|jpe?g|webp)$/i.test(val("enunciado"))) {
          archivo = val("enunciado")
        }
        const nombreCurso = val("curso")
        if (!nombreCurso) continue
        if (!cursos[nombreCurso]) {
          cursos[nombreCurso] = {
            nombre: nombreCurso,
            marca: (val("marca") || "inbi").toLowerCase() === "fast" ? "fast" : "inbi",
            estado: val("estado") === "publicado" ? "publicado" : "proximamente",
            subtitulo: val("subtitulo") || "Desde cero",
            descripcion: "",
            niveles: {},
            ordenNiveles: [],
          }
          orden.push(nombreCurso)
        }
        const curso = cursos[nombreCurso]
        if (tipo === "curso") {
          if (val("marca")) curso.marca = val("marca").toLowerCase() === "fast" ? "fast" : "inbi"
          if (val("estado") === "publicado") curso.estado = "publicado"
          curso.descripcion = val("descripcion") || curso.descripcion
          continue
        }
        const nombreNivel = val("nivel")
        if (!nombreNivel) continue
        if (!curso.niveles[nombreNivel]) {
          curso.niveles[nombreNivel] = vacioNivel(nombreNivel)
          curso.ordenNiveles.push(nombreNivel)
        }
        const nivel = curso.niveles[nombreNivel]
        if (tipo === "nivel") {
          nivel.descripcion = val("descripcion") || nivel.descripcion
          // Compat CSV viejo: una sola fila "nivel" con video = video principal
          if (val("archivo") || val("miniatura") || val("titulo")) {
            nivel.videoPrincipal.titulo = val("titulo") || "Video principal"
            nivel.videoPrincipal.descripcion = val("descripcion") || ""
            nivel.videoPrincipal.archivo = val("archivo") || ""
            nivel.videoPrincipal.miniatura = val("miniatura") || ""
          }
        } else if (tipo === "video_principal" || tipo === "video") {
          nivel.videoPrincipal.titulo = val("titulo") || "Video principal"
          nivel.videoPrincipal.descripcion = val("descripcion") || ""
          nivel.videoPrincipal.archivo = val("archivo") || ""
          nivel.videoPrincipal.miniatura = val("miniatura") || ""
        } else if (tipo === "video_explicacion") {
          nivel.videoExplicacion.titulo = val("titulo") || "Video de explicación"
          nivel.videoExplicacion.descripcion = val("descripcion") || ""
          nivel.videoExplicacion.archivo = val("archivo") || ""
          nivel.videoExplicacion.miniatura = val("miniatura") || ""
        } else if (tipo === "gramatica") {
          if (val("descripcion") && !nivel.explicacion) nivel.explicacion = val("descripcion")
          const otras = [val("opcion_1"), val("opcion_2"), val("opcion_3")].filter((x) => x && x !== val("palabra"))
          nivel.gramaticaLista.push({
            enunciado: val("enunciado"),
            palabra: val("palabra"),
            opciones: [val("palabra"), otras[0] || "", otras[1] || ""],
            descripcion: val("descripcion"),
          })
        } else if (tipo === "habla") {
          nivel.hablaLista.push({ texto: texto, archivo: archivo })
        } else if (tipo === "escribe") {
          nivel.escribeLista.push({ texto: texto, archivo: archivo })
        } else if (tipo === "vocabulario") {
          nivel.vocabLista.push({
            archivo: archivo,
            opciones: [op1, op2, op3],
          })
        }
      }
      const marcarVideo = (video) => {
        const videoNombre = String(video.archivo || "").toLowerCase()
        const miniNombre = String(video.miniatura || "").toLowerCase()
        video.faltante = !!(videoNombre && !presentes.has(videoNombre))
        video.faltanteMini = !!(miniNombre && !presentes.has(miniNombre))
        return Object.assign({}, video, { miniUrl: urlDe(video.miniatura) })
      }
      const vista = orden.map((nombre) => {
        const curso = cursos[nombre]
        const ya = this.existentes.find((c) => String(c.nombre).toLowerCase() === nombre.toLowerCase() && c.marca === curso.marca)
        return {
          nombre: curso.nombre,
          marca: curso.marca,
          reescribe: !!ya,
          niveles: curso.ordenNiveles.map((nombreNivel) => {
            const nivel = curso.niveles[nombreNivel]
            const nombresYa = ya && ya._niveles ? ya._niveles : []
            const frases = nivel.gramaticaLista.map((ej) => {
              const partes = String(ej.enunciado || "").trim().split(/\s+/).filter(Boolean)
              const i = partes.findIndex((p) => p.toLowerCase() === String(ej.palabra || "").toLowerCase())
              if (i >= 0) partes[i] = "______"
              return partes.join(" ")
            })
            return {
              nombre: nivel.nombre,
              descripcion: nivel.descripcion,
              reescribe: nombresYa.indexOf(nombreNivel.toLowerCase()) >= 0,
              videoPrincipal: marcarVideo(nivel.videoPrincipal),
              videoExplicacion: marcarVideo(nivel.videoExplicacion),
              gramatica: nivel.gramaticaLista.length,
              habla: nivel.hablaLista.length,
              escribe: nivel.escribeLista.length,
              vocabulario: nivel.vocabLista.length,
              frases: frases,
              vocabFotos: nivel.vocabLista.map((it) => urlDe(it.archivo)).filter(Boolean),
            }
          }),
        }
      })
      if (!vista.length) {
        this.error = "El CSV no trae un curso"
        return
      }
      this.preview = { cursos: vista }
      this.manifest = {
        marca: vista[0].marca,
        cursos: orden.map((nombre) => {
          const curso = cursos[nombre]
          return {
            nombre: curso.nombre,
            marca: curso.marca,
            estado: curso.estado,
            subtitulo: curso.subtitulo,
            descripcion: curso.descripcion,
            niveles: curso.ordenNiveles.map((nombreNivel) => {
              const nivel = curso.niveles[nombreNivel]
              const lecciones = []
              if (nivel.videoPrincipal.archivo || nivel.videoPrincipal.titulo || nivel.videoPrincipal.miniatura) {
                lecciones.push({
                  titulo: "Video principal",
                  descripcion: nivel.videoPrincipal.descripcion || "",
                  archivo: nivel.videoPrincipal.archivo || "",
                  miniatura: nivel.videoPrincipal.miniatura || "",
                })
              }
              if (nivel.videoExplicacion.archivo || nivel.videoExplicacion.titulo || nivel.videoExplicacion.miniatura) {
                lecciones.push({
                  titulo: "Video de explicación",
                  descripcion: nivel.videoExplicacion.descripcion || "",
                  archivo: nivel.videoExplicacion.archivo || "",
                  miniatura: nivel.videoExplicacion.miniatura || "",
                })
              }
              return {
                nombre: nivel.nombre,
                descripcion: nivel.descripcion,
                lecciones: lecciones,
                gramatica: { explicacion: nivel.explicacion, ejercicios: nivel.gramaticaLista },
                habla: nivel.hablaLista,
                escribe: nivel.escribeLista,
                vocabulario: nivel.vocabLista,
              }
            }),
          }
        }),
      }
    },
    aceptar() {
      if (!this.manifest) return
      this.subiendo = true
      this.error = ""
      const form = new FormData()
      form.append("manifest", JSON.stringify(this.manifest))
      this.archivos.forEach((f) => form.append("archivo", f, this.nombreArchivo(f)))
      this.$http.post("curso.virtual.carga", form).then(() => {
        this.$emit("listo")
        this.$emit("input", false)
        this.preview = null
        this.manifest = null
      }).catch((e) => {
        const body = (e && e.body) || (e && e.response && e.response.data) || {}
        this.error = body.message || "No se pudo subir"
      }).finally(() => {
        this.subiendo = false
      })
    },
  },
}
</script>

<style scoped>
.hint { color: #5c6b73; font-size: 14px; margin: 0 0 6px; }
.referencia { display: inline-block; margin: 4px 0 8px; padding: 0; border: 0; background: transparent; color: #0e7c78; font-weight: 700; cursor: pointer; }
.ref-nivel { border: 1px solid #d7e6e4; border-radius: 12px; padding: 12px 14px; margin-bottom: 10px; }
.ref-nivel h4 { margin: 2px 0 4px; }
.ref-linea { margin: 0 0 4px; color: #334155; }
.csv-ref { max-height: 220px; overflow: auto; background: #f4f7f7; border-radius: 8px; padding: 10px; font-size: 12px; white-space: pre; }
.mono { font-family: Consolas, monospace; font-size: 12px; }
.curso-bloque { margin-bottom: 18px; }
.curso-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.curso-head h3 { margin: 0; font-size: 20px; }
.marca { margin: 0; font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: #0e7c78; font-weight: 800; }
.nivel-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
.nivel-card { background: #fff; border: 1px solid #d7e6e4; border-radius: 16px; overflow: hidden; }
.nivel-foto { min-height: 148px; background: #e7f5f3; display: grid; place-items: center; color: #5c6b73; }
.nivel-foto.chica { width: 88px; min-height: 66px; height: 66px; border-radius: 10px; overflow: hidden; flex-shrink: 0; }
.nivel-foto img { width: 100%; height: 100%; object-fit: cover; }
.nivel-cuerpo { padding: 12px 14px 14px 14px; }
.nivel-cuerpo.solo { padding: 14px 16px; }
.leccion-mini { display: flex; gap: 12px; align-items: flex-start; margin: 0 0 10px; padding: 8px 0; border-top: 1px solid #e8f0ef; }
.leccion-mini h5 { margin: 2px 0 2px; font-size: 15px; }
.kicker { margin: 0; font-size: 12px; color: #0e7c78; font-weight: 700; }
.nivel-cuerpo h4 { margin: 2px 0 4px; font-size: 18px; }
.desc { margin: 0 0 8px; color: #526066; }
.chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.chips span { background: #eef6f5; color: #0e7c78; border-radius: 999px; padding: 4px 8px; font-size: 12px; font-weight: 700; }
.frases { margin: 0 0 8px; padding-left: 18px; }
.vocab-fila { display: flex; gap: 8px; margin-bottom: 8px; }
.vocab-fila img { width: 54px; height: 54px; border-radius: 50%; object-fit: cover; }
.examen { margin: 0; color: #526066; font-size: 13px; }
.aviso { color: #9a5b00; }
.acciones { display: flex; align-items: center; }
</style>
